import { useRouter } from 'next/router';

import { Alert, Box, Heading, List, Paragraph } from '@zillow/constellation-10';

import accessUtils from '../../../../../srcLegacy/shared/utils/accessUtils';
import { useGetListingDetails } from '../../../../hooks/queries/properties';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import PropertyDescriptionFormField from '../../../PostingPath/PropertyDescriptionFormField';
import PageWizard from '../../../Wizard/PageWizard';
import WizardTipCardContainer from '../../../WizardTipCardContainer';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const DescribeProperty = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const shouldShowBrokerFeeAlert = accessUtils.shouldShowBrokerFeeAlert({
    state: listingDetails?.state ?? '',
  });

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
    >
      <div data-testid="DescribeProperty">
        <Box css={wizardFlexContainerStyles}>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
              Describe the property
            </Heading>

            <Paragraph css={wizardParagraphStyles}>
              Write several sentences describing the upgrades and desirable features that will attract renters to your
              property.
            </Paragraph>

            {shouldShowBrokerFeeAlert && (
              <Box css={{ marginBottom: '400' }}>
                <Alert tone="info">
                  <strong>MA Broker Fee Rule:</strong> Starting Aug 1, broker fees can be charged only to tenants who
                  directly hire a broker
                </Alert>
              </Box>
            )}

            <PropertyDescriptionFormField />
          </Box>

          <WizardTipCardContainer
            header="Tips for property descriptions"
            showDesktopViewBreakpoint={showDesktopViewBreakpoint}
          >
            <List.Root appearance="bullet">
              <List.Item>
                Market the property’s proximity to transit, dining, shopping, and other local attractions
              </List.Item>
              <List.Item>Mention upgrades, attractive amenities, and other appealing details</List.Item>
              <List.Item>Indicate whether you live on-site</List.Item>
            </List.Root>
          </WizardTipCardContainer>
        </Box>
      </div>
    </PageWizard>
  );
};

export default DescribeProperty;
